import { useEffect, useRef, useState } from 'react';
import apiClient from '../../utils/apiclient';
import * as XLSX from 'xlsx';

const Index = () => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedEntityId, setSelectedEntityId] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [excelNumberCount, setExcelNumberCount] = useState(0);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [sendMessage, setSendMessage] = useState('');
    const [sendError, setSendError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const response = await apiClient.get('/Bulkwhatsapp-message');
                setMessages(Array.isArray(response.data) ? response.data : []);
            } catch (fetchError) {
                console.error('Error fetching bulk WhatsApp messages:', fetchError);
                setError('Unable to load bulk WhatsApp messages.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchMessages();
    }, []);

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0] || null;
        setSelectedFile(file);
        setExcelNumberCount(0);
        setSendError('');

        if (!file) return;

        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
            const numberCount = rows.flat().filter((value) => {
                if (typeof value === 'number') return Number.isFinite(value);
                return /^[+]?\d+(?:[.,]\d+)?$/.test(String(value).trim());
            }).length;

            setExcelNumberCount(numberCount);
        } catch (fileError) {
            console.error('Error reading Excel file:', fileError);
            setSelectedFile(null);
            setSendError('Unable to read the selected Excel file.');
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSend = async (event) => {
        event.preventDefault();
        setSendMessage('');
        setSendError('');

        if (!selectedEntityId || !selectedFile) {
            setSendError('Please select a template and an Excel file.');
            return;
        }

        const estimatedCost = (excelNumberCount * 0.25).toFixed(2);
        const shouldSend = window.confirm(
            `Send this message to ${excelNumberCount} numbers?\nEstimated cost: INR ${estimatedCost}`
        );
        if (!shouldSend) return;

        try {
            setIsSending(true);
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('entityId', selectedEntityId);

            const response = await apiClient.post('/Bulkwhatsapp-message/send', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSendMessage(response.data || 'WhatsApp messages are being processed');
            setSelectedEntityId('');
            setSelectedTemplate(null);
            setSelectedFile(null);
            setExcelNumberCount(0);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (sendErrorResponse) {
            console.error('Error sending bulk WhatsApp messages:', sendErrorResponse);
            setSendError(sendErrorResponse.response?.data || 'Unable to send bulk WhatsApp messages.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="w-full flex-1 flex flex-col min-h-0 mt-4">
            <div className="flex justify-between items-center mb-2 flex-shrink-0">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Bulk WhatsApp Messages</h2>
                    <p className="text-sm text-gray-500">Available WhatsApp templates</p>
                </div>
                {!isLoading && <span className="text-sm text-gray-500">{messages.length} templates</span>}
            </div>

            <form onSubmit={handleSend} className="mb-4 border border-gray-300 bg-white p-4 shadow-sm">
                <div className="mb-3">
                    <h3 className="text-base font-bold text-gray-800">Send Bulk WhatsApp Messages</h3>
                    <p className="text-sm text-gray-500">Choose a template and upload an Excel file with recipient details.</p>
                </div>
                <div className="flex flex-col gap-3 md:flex-row md:items-end">
                    <label className="flex flex-1 flex-col gap-1 text-sm font-semibold text-gray-700">
                        WhatsApp Template
                        <select
                            value={selectedEntityId}
                            onChange={(event) => {
                                const entityId = event.target.value;
                                setSelectedEntityId(entityId);
                                setSelectedTemplate(messages.find((message) => String(message.entityId) === entityId) || null);
                            }}
                            className="rounded border border-gray-400 px-3 py-2 font-normal focus:border-blue-500 focus:outline-none"
                            disabled={isSending || isLoading}
                        >
                            <option value="">Select a template</option>
                            {messages.map((message) => (
                                <option key={message.id} value={message.entityId}>
                                    {message.templateName} ({message.entityId})
                                </option>
                            ))}
                        </select>
                    </label>
                    {selectedTemplate && (
                        <div className="flex-1 border border-blue-200 bg-blue-50 p-3 text-sm text-gray-800">
                            <p className="mb-2 font-bold text-blue-900">Selected template details</p>
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                                {Object.entries(selectedTemplate).map(([key, value]) => (
                                    <div key={key}>
                                        <dt className="text-xs font-semibold uppercase text-gray-500">{key}</dt>
                                        <dd className="whitespace-pre-wrap break-words">
                                            {typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : String(value ?? '-')}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    )}
                    <label className="flex flex-1 flex-col gap-1 text-sm font-semibold text-gray-700">
                        Excel File
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            className="rounded border border-gray-400 px-3 py-1.5 font-normal file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1 file:text-sm"
                            disabled={isSending}
                        />
                    </label>
                    {selectedFile && (
                        <div className="text-sm text-gray-600 md:w-44">
                            <p>{excelNumberCount} numbers found</p>
                            <p className="font-semibold text-gray-800">Estimated: INR {(excelNumberCount * 0.25).toFixed(2)}</p>
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={isSending || isLoading || messages.length === 0}
                        className="rounded bg-green-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                        {isSending ? 'Sending...' : 'Send Messages'}
                    </button>
                </div>
                {sendMessage && <p className="mt-3 text-sm font-medium text-green-700">{sendMessage}</p>}
                {sendError && <p className="mt-3 text-sm font-medium text-red-600">{sendError}</p>}
            </form>

            <div className="w-full flex-1 overflow-auto pb-4 relative">
                {isLoading && <p className="p-4 text-sm text-gray-500">Loading messages...</p>}
                {!isLoading && error && <p className="p-4 text-sm text-red-600">{error}</p>}
                {!isLoading && !error && messages.length === 0 && (
                    <p className="p-4 text-sm text-gray-500">No WhatsApp message templates found.</p>
                )}
                {!isLoading && !error && messages.length > 0 && (
                    <table className="min-w-[1100px] w-full bg-white shadow-md border-collapse">
                        <thead className="bg-gray-800 text-white sticky top-0 z-20 shadow-md">
                            <tr>
                                <th className="px-3 py-3 text-xs font-semibold text-left">Template</th>
                                <th className="px-3 py-3 text-xs font-semibold text-left">Message ID</th>
                                <th className="px-3 py-3 text-xs font-semibold text-left">Template ID</th>
                                <th className="px-3 py-3 text-xs font-semibold text-left">Sender Number</th>
                                <th className="px-3 py-3 text-xs font-semibold text-left">Media Type</th>
                                <th className="px-3 py-3 text-xs font-semibold text-left">Message</th>
                                <th className="px-3 py-3 text-xs font-semibold text-left">Entity ID</th>
                            </tr>
                        </thead>
                        <tbody>
                            {messages.map((message) => (
                                <tr
                                    key={message.id}
                                    className="border border-gray-200 align-top hover:bg-gray-50 cursor-pointer"
                                    onClick={() => setSelectedTemplate(message)}
                                >
                                    <td className="px-3 py-3 text-sm font-semibold text-gray-800">{message.templateName || '-'}</td>
                                    <td className="px-3 py-3 text-sm text-gray-700">{message.messageId || '-'}</td>
                                    <td className="px-3 py-3 text-sm text-gray-700">{message.templateId || '-'}</td>
                                    <td className="px-3 py-3 text-sm text-gray-700">{message.senderNumber || '-'}</td>
                                    <td className="px-3 py-3 text-sm text-gray-700">{message.mediaType || '-'}</td>
                                    <td className="px-3 py-3 text-sm text-gray-700 whitespace-pre-wrap min-w-[420px]">{message.messageText || '-'}</td>
                                    <td className="px-3 py-3 text-sm text-gray-700">{message.entityId || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {selectedTemplate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelectedTemplate(null)}>
                    <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded bg-white p-5 shadow-xl" onClick={(event) => event.stopPropagation()}>
                        <div className="mb-4 flex items-center justify-between gap-4">
                            <h3 className="text-lg font-bold text-gray-800">{selectedTemplate.templateName || 'Template overview'}</h3>
                            <button type="button" className="text-sm font-semibold text-gray-600 hover:text-gray-900" onClick={() => setSelectedTemplate(null)}>
                                Close
                            </button>
                        </div>
                        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {Object.entries(selectedTemplate).map(([key, value]) => (
                                <div key={key} className="border-b border-gray-200 pb-2">
                                    <dt className="text-xs font-semibold uppercase text-gray-500">{key}</dt>
                                    <dd className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800">
                                        {typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : String(value ?? '-')}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Index;